import type React from 'react';
import {
  AimOutlined,
  ApartmentOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BankOutlined,
  BarChartOutlined,
  BgColorsOutlined,
  CarOutlined,
  CheckCircleOutlined,
  ClusterOutlined,
  CommentOutlined,
  CompassOutlined,
  ControlOutlined,
  CreditCardOutlined,
  CrownOutlined,
  DashboardOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  ExceptionOutlined,
  ExperimentOutlined,
  ExportOutlined,
  FileAddOutlined,
  FileExclamationOutlined,
  FileProtectOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  FlagOutlined,
  FolderOpenOutlined,
  FundOutlined,
  FundProjectionScreenOutlined,
  GiftOutlined,
  GlobalOutlined,
  GroupOutlined,
  HeatMapOutlined,
  HistoryOutlined,
  HomeOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  LayoutOutlined,
  LineChartOutlined,
  LockOutlined,
  MailOutlined,
  MessageOutlined,
  MonitorOutlined,
  NodeIndexOutlined,
  NotificationOutlined,
  OrderedListOutlined,
  PartitionOutlined,
  PercentageOutlined,
  PictureOutlined,
  PlusCircleOutlined,
  PlusSquareOutlined,
  ProfileOutlined,
  QuestionCircleOutlined,
  RightOutlined,
  RiseOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  SafetyOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  SlidersOutlined,
  SoundOutlined,
  StarOutlined,
  StopOutlined,
  TagsOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  TrophyOutlined,
  UnorderedListOutlined,
  UserOutlined,
  WalletOutlined,
  WarningOutlined,
} from '@ant-design/icons';

/**
 * Icons for the sidebar's sub-menu rows (theme `.nav--sub` carries an icon on
 * every child). Keyed by the MenuItem `key` from Utilis/menuConfig.ts; an item
 * that sets its own `icon` there still wins. Outlined antd icons paint with
 * currentColor, so they follow the row's text and active colours.
 */
export const SUBMENU_ICON_MAP: Record<string, React.ComponentType<any>> = {
  // Settings
  'all-companies': BankOutlined,
  'company-customizations': BgColorsOutlined,
  'company-setting': ControlOutlined,
  users: UserOutlined,
  'notification-sounds': SoundOutlined,
  'payment-settings': CreditCardOutlined,
  'customer-segmentation': ClusterOutlined,
  'export-reports': ExportOutlined,
  'email-settings': MailOutlined,
  'push-notifications': NotificationOutlined,
  organization: ApartmentOutlined,
  'sms-templates': MessageOutlined,

  // Permissions
  'permissions-models': AppstoreOutlined,
  'permissions-permissions': KeyOutlined,
  'permissions-roles': SafetyCertificateOutlined,

  // Orders
  addOrder: PlusCircleOutlined,
  orderList: UnorderedListOutlined,
  activeOrders: ThunderboltOutlined,
  orderTracking: AimOutlined,
  'insurance-order': FileProtectOutlined,

  // Activities
  allTickets: ProfileOutlined,
  activityTypes: AppstoreOutlined,
  activitySections: LayoutOutlined,
  activityReasons: QuestionCircleOutlined,
  activityStatus: FlagOutlined,

  // Catalog
  product: ShoppingOutlined,
  categories: PartitionOutlined,
  brands: CrownOutlined,
  extra: PlusSquareOutlined,
  productAttributes: SlidersOutlined,
  keywords: TagsOutlined,
  fileManager: FolderOpenOutlined,
  suppliers: ShopOutlined,

  // Salla orders
  'salla-orders-all': UnorderedListOutlined,
  'salla-orders-purchased': CheckCircleOutlined,

  // Reports
  'reports-main': FileTextOutlined,
  'reports-dashboard': DashboardOutlined,
  'reports-export-logs': HistoryOutlined,
  'reports-companies': BankOutlined,
  'reports-store-compare': BarChartOutlined,
  'reports-growth': RiseOutlined,
  'reports-products': ShoppingOutlined,
  'reports-customers': TeamOutlined,
  'reports-order-analytics': LineChartOutlined,
  'reports-revenue': DollarOutlined,

  // Insurance
  'insurance-companies': SafetyOutlined,

  // Pages
  about: InfoCircleOutlined,
  privacy: LockOutlined,
  terms: AuditOutlined,
  faq: QuestionCircleOutlined,

  // Offers
  'all-offers': GiftOutlined,
  'offers-group': GroupOutlined,

  // E-Commerce
  slider: PictureOutlined,
  cancelReasons: StopOutlined,
  sections: LayoutOutlined,
  flashDeal: ThunderboltOutlined,
  promocode: PercentageOutlined,
  homeStaticContent: HomeOutlined,

  // Locations
  countries: GlobalOutlined,
  cities: EnvironmentOutlined,
  areas: CompassOutlined,
  regions: HeatMapOutlined,

  // Logs
  failedProductImports: FileExclamationOutlined,
  failedOfferImports: ExceptionOutlined,
  failedCategoryImports: WarningOutlined,
  integrationMonitoring: MonitorOutlined,

  // Integrations
  sms: CommentOutlined,
  paymentGateway: WalletOutlined,

  // DMS settings
  'ai-distribution': RobotOutlined,
  skills: StarOutlined,
  shoppers: ShoppingCartOutlined,
  'shopper-order-status': OrderedListOutlined,
  'service-types': ToolOutlined,
  'trip-types': CarOutlined,
  custody: InboxOutlined,

  // Purchasing
  'predict-products': ExperimentOutlined,
  'purchasing-requests': FileAddOutlined,
  'all-purchasing-requests': FileSearchOutlined,

  // DMS reports
  'dms-dashboard': DashboardOutlined,
  'top-10-shoppers': TrophyOutlined,
  'driver-tracker': NodeIndexOutlined,
  'agent-kpi': FundOutlined,
  prediction: FundProjectionScreenOutlined,
};

/** Any child row added to the config later without an icon still gets one. */
export const SUBMENU_FALLBACK_ICON = RightOutlined;
